import { Row, Col } from 'antd';
import { 
    ShoppingCartOutlined, 
    CheckCircleOutlined, 
    CloseCircleOutlined 
} from '@ant-design/icons';
import StatCard from '@/components/UI/Cards/StatCard';
import { useProductData } from '../hooks/useProductData';

/**
 * Product statistics card component
 * Displays product statistics for BulkPurchase management
 */
const BulkPurchaseCard = ({ activeTab = 0 }) => {
    // Use custom hook for product data
    const { 
        foodProducts,
        platterProducts,
        nonEdiblesProducts,
        totalFood,
        totalPlatter,
        totalNonEdibles,
        totalProducts,
        isLoading, 
        isError,
        error 
    } = useProductData(activeTab);

    if (isLoading) {
        return (
            <Row gutter={[16, 16]}>
                {[1, 2, 3].map((index) => (
                    <Col xs={24} sm={12} md={8} key={index}>
                        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center space-x-4 shadow-sm">
                            <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse"></div>
                            <div className="flex flex-col flex-1">
                                <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-24"></div>
                                <div className="h-6 bg-gray-200 rounded animate-pulse w-12"></div>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        );
    }

    if (isError) {
        return (
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                        <p className="text-red-600">
                            Error loading products data: {error?.message || 'Unknown error'}
                        </p>
                    </div>
                </Col>
            </Row>
        );
    }

    // Get current tab data based on activeTab
    const getCurrentTabData = () => {
        switch (activeTab) {
            case 0: // Products
                return {
                    products: foodProducts || [],
                    total: totalFood ?? 0,
                    categoryName: 'Products'
                };
            case 1: // Platter
                return {
                    products: platterProducts || [],
                    total: totalPlatter ?? 0,
                    categoryName: 'Platters'
                };
            case 2: // Non-edibles
                return {
                    products: nonEdiblesProducts || [],
                    total: totalNonEdibles ?? 0,
                    categoryName: 'Non-edibles'
                };
            default:
                return {
                    products: foodProducts || [],
                    total: totalFood ?? 0,
                    categoryName: 'Products'
                };
        }
    };

    const currentTabData = getCurrentTabData();
    const activeProducts = currentTabData.products.filter(product => product.status === 1).length;
    const inactiveProducts = currentTabData.products.filter(product => product.status === 0).length;

    // Data configuration for tab-specific statistics
    const cardData = [
        {
            icon: ShoppingCartOutlined,
            title: `All ${currentTabData.categoryName}`,
            count: currentTabData.total,
            iconColor: "text-blue-600",
            iconBgColor: "bg-blue-100"
        },
        {
            icon: CheckCircleOutlined,
            title: `Active ${currentTabData.categoryName}`,
            count: activeProducts,
            iconColor: "text-green-600",
            iconBgColor: "bg-green-100"
        },
        {
            icon: CloseCircleOutlined,
            title: `Inactive ${currentTabData.categoryName}`,
            count: inactiveProducts,
            iconColor: "text-red-600",
            iconBgColor: "bg-red-100"
        }
    ];

    return (
        <Row gutter={[16, 16]}>
            {cardData.map((stat, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                    <StatCard
                        icon={stat.icon}
                        title={stat.title}
                        count={stat.count}
                        iconColor={stat.iconColor}
                        iconBgColor={stat.iconBgColor}
                    />
                </Col>
            ))}
        </Row>
    );
};

export default BulkPurchaseCard;